import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import {
    FaStar,
    FaIndustry,
    FaHandshake,
    FaGlobe,
    FaAward,
    FaUsers,
    FaChevronLeft,
    FaChevronRight,
    FaBuilding,
    FaOilCan,
    FaHotel,
    FaCogs,
    FaClock
} from 'react-icons/fa';

const BrandsPartner = () => {
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true
    });

    const navigate = useNavigate();

    // Navigation handler functions
    const handleBecomePartner = () => {
        navigate('/contact');
    };

    const handleViewCaseStudies = () => {
        navigate('/about');
    };

    const handleStatsClick = (statType) => {
        const navigationMap = {
            'partners': '/about',
            'countries': '/about',
            'experience': '/about',
            'projects': '/service'
        };
        navigate(navigationMap[statType] || '/about');
    };

    const handleClientClick = (clientId) => {
        navigate('/about', { state: { clientSpotlight: clientId } });
    };

    const handleIndustryClick = (industryType) => {
        navigate('/service', { state: { industryFilter: industryType } });
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                duration: 0.8
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30, scale: 0.9 },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    const logoVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: {
            opacity: 1,
            scale: 1,
            transition: {
                duration: 0.5,
                ease: "easeOut"
            }
        }
    };

    // Updated clients list - Saudi Aramco removed
    const clients = [
        {
            id: 1,
            name: "PANDA",
            category: "Retail & Commerce",
            industry: "Retail",
            icon: FaBuilding,
            description: "Leading retail chain providing quality products across the region",
            image: "/partners/panda.webp"
        },
        {
            id: 2,
            name: "Musaed El Seif Contracting",
            category: "Construction & Infrastructure",
            industry: "Construction",
            icon: FaBuilding,
            description: "Premier construction company delivering world-class infrastructure projects",
            image: "/partners/el_seif.webp"
        },
        {
            id: 3,
            name: "LE MERIDIEN HOTEL",
            category: "Hospitality & Tourism",
            industry: "Hospitality",
            icon: FaHotel,
            description: "Luxury hospitality brand offering exceptional guest experiences",
            image: "/partners/le_meridien.webp"
        },
        {
            id: 4,
            name: "MARRIOTT HOTEL",
            category: "Hospitality & Tourism",
            industry: "Hospitality",
            icon: FaHotel,
            description: "Premium hotel chain focused on comfort and luxury accommodation",
            image: "/partners/marriott.webp"
        },
        {
            id: 5,
            name: "JGC Corporation",
            category: "Engineering & Construction",
            industry: "Engineering",
            icon: FaCogs,
            description: "Global engineering, procurement and construction company",
            image: "/partners/jgc.webp"
        },
        {
            id: 6,
            name: "HYUNDAI ENGINEERING",
            category: "Engineering & Construction",
            industry: "Engineering",
            icon: FaCogs,
            description: "Leading engineering and construction solutions provider",
            image: "/partners/hyundai.webp"
        },
        {
            id: 7,
            name: "SAIPEM",
            category: "Oil & Gas Services",
            industry: "Energy Services",
            icon: FaOilCan,
            description: "Advanced technological solutions for the energy industry",
            image: "/partners/saipem.webp"
        },
        {
            id: 8,
            name: "SINOHYDRO",
            category: "Water & Power",
            industry: "Infrastructure",
            icon: FaBuilding,
            description: "Global leader in water conservancy and hydropower engineering",
            image: "/partners/sinohydro.webp"
        },
        {
            id: 9,
            name: "SEPCO",
            category: "Power & Energy",
            industry: "Power",
            icon: FaCogs,
            description: "Specialized in power plant construction and energy solutions",
            image: "/partners/sepco.webp"
        },
        {
            id: 10,
            name: "SABIC",
            category: "Chemicals & Petrochemicals",
            industry: "Chemicals",
            icon: FaIndustry,
            description: "Global leader in diversified chemicals and innovative materials",
            image: "/partners/sabic.webp"
        },
        {
            id: 11,
            name: "SINOPEC",
            category: "Oil & Gas",
            industry: "Energy",
            icon: FaOilCan,
            description: "Major petroleum and petrochemical enterprise group",
            image: "/partners/sinopec.webp"
        }
    ];

    // Updated stats - changed experience to 20+ years
    const stats = [
        {
            number: "11+",
            label: "Trusted Partners",
            icon: FaHandshake,
            type: "partners"
        },
        {
            number: "50+",
            label: "Countries Served",
            icon: FaGlobe,
            type: "countries"
        },
        {
            number: "20+",
            label: "Years Experience",
            icon: FaAward,
            type: "experience"
        },
        {
            number: "1000+",
            label: "Projects Completed",
            icon: FaUsers,
            type: "projects"
        }
    ];

    // Brand images with multiple format support
    const brandImages = Array.from({ length: 17 }, (_, i) => {
        const brandId = i + 1;
        const possibleExtensions = ['webp', 'png', 'jpg', 'jpeg', 'gif', 'jfif'];

        return {
            id: brandId,
            sources: possibleExtensions.map(ext => `/brand/brand-${brandId}.${ext}`),
            alt: `Brand Partner ${brandId}`
        };
    });

    // Create scrolling animation variants
    const scrollVariants = {
        animate: {
            x: [0, -3080], // Adjusted for 22 images (140px each)
            transition: {
                x: {
                    repeat: Infinity,
                    repeatType: "loop",
                    duration: 50, // Slower for better brand visibility
                    ease: "linear",
                },
            },
        },
    };

    // Duplicate brand images for seamless scrolling
    const scrollingBrands = [...brandImages, ...brandImages];

    // Image component with multiple format support - No cards, direct display
    const BrandImage = ({ brand, index }) => {
        const [currentSourceIndex, setCurrentSourceIndex] = React.useState(0);
        const [imageLoaded, setImageLoaded] = React.useState(false);

        const handleImageError = () => {
            if (currentSourceIndex < brand.sources.length - 1) {
                setCurrentSourceIndex(currentSourceIndex + 1);
            }
        };

        const handleImageLoad = () => {
            setImageLoaded(true);
        };

        return (
            <div
                key={`${brand.id}-${index}`}
                className="flex-shrink-0 w-32 h-20 md:w-36 md:h-24 flex items-center justify-center mx-2 cursor-pointer hover:scale-110 transition-transform duration-300"
            >
                {!imageLoaded && currentSourceIndex >= brand.sources.length - 1 ? (
                    // Simple fallback when all formats fail
                    <div className="w-full h-full flex items-center justify-center text-gray-300 text-xs border border-gray-200 rounded-lg bg-gray-50">
                        Brand {brand.id}
                    </div>
                ) : (
                    <img
                        src={brand.sources[currentSourceIndex]}
                        alt={brand.alt}
                        className="max-w-full max-h-full object-contain filter hover:brightness-110 transition-all duration-300"
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                        loading="lazy"
                    />
                )}
            </div>
        );
    };

    return (
        <section className="py-20 md:py-28 bg-white relative overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 opacity-20">
                <div className="absolute top-20 left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
                <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-blue-200/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '4s' }}></div>
            </div>

            <div className="container mx-auto px-4 md:px-8 relative z-10">
                {/* Header Section */}
                <motion.div
                    ref={ref}
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="text-center mb-16 md:mb-20"
                >
                    <motion.div variants={itemVariants} className="inline-flex items-center gap-2 bg-secondary px-5 py-3 rounded-full mb-8 shadow-lg">
                        <FaHandshake className="text-primary text-base" />
                        <span className="text-primary font-bold text-sm uppercase tracking-wider">
                            AKTP Trusted Partnership
                        </span>
                    </motion.div>

                    <motion.h2
                        variants={itemVariants}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-6 leading-tight"
                    >
                        Our Valued
                        <span className="block text-secondary drop-shadow-lg">Brand Partners</span>
                    </motion.h2>

                    <motion.p
                        variants={itemVariants}
                        className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
                    >
                        Proudly serving industry leaders across oil & gas, construction, hospitality, and manufacturing sectors with premium industrial solutions during our business hours
                    </motion.p>
                </motion.div>

                {/* Stats Section */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-20"
                >
                    {stats.map((stat, index) => (
                        <motion.div
                            key={index}
                            variants={itemVariants}
                            onClick={() => handleStatsClick(stat.type)}
                            className="bg-white rounded-2xl p-6 text-center shadow-xl border border-gray-100 hover:shadow-2xl transition-all duration-300 group cursor-pointer"
                            whileHover={{ y: -5 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-secondary transition-colors duration-300">
                                <stat.icon className="text-white text-xl group-hover:text-primary" />
                            </div>
                            <h3 className="text-2xl md:text-3xl font-bold text-primary mb-2">{stat.number}</h3>
                            <p className="text-gray-600 font-medium">{stat.label}</p>
                        </motion.div>
                    ))}
                </motion.div>

                {/* Direct Brand Logos Scrolling Section - No Cards */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="mb-20"
                >
                    <motion.h3
                        variants={itemVariants}
                        className="text-2xl md:text-3xl font-bold text-center text-primary mb-12"
                    >
                        Trusted Brands We Supply
                    </motion.h3>


                    <div className="relative overflow-hidden py-8 bg-gradient-to-r from-gray-100 via-gray-50 to-gray-100 rounded-2xl border border-gray-200">
                        {/* Gradient overlays for fade effect */}
                        <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-gray-100 via-gray-50 to-transparent z-10"></div>
                        <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-gray-100 via-gray-50 to-transparent z-10"></div>

                        <motion.div
                            className="flex items-center"
                            variants={scrollVariants}
                            animate="animate"
                            style={{ width: 'max-content' }}
                        >
                            {scrollingBrands.map((brand, index) => (
                                <BrandImage key={`${brand.id}-${index}`} brand={brand} index={index} />
                            ))}
                        </motion.div>
                    </div>

                    <motion.p
                        variants={itemVariants}
                        className="text-center text-gray-600 mt-8 text-lg"
                    >
                        Premium industrial brands and materials available across Saudi Arabia
                    </motion.p>
                </motion.div>

                {/* Client Logos Grid */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="mb-16"
                >
                    <motion.h3
                        variants={itemVariants}
                        className="text-2xl md:text-3xl font-bold text-center text-primary mb-12"
                    >
                        Our Partner Network
                    </motion.h3>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
                        {clients.map((client, index) => (
                            <motion.div
                                key={client.id}
                                variants={logoVariants}
                                whileHover={{
                                    scale: 1.05,
                                    transition: { duration: 0.3 }
                                }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleClientClick(client.id)}
                                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 cursor-pointer"
                            >
                                <div className="text-center">
                                    <div className="mb-4 flex justify-center">
                                        <div className="w-24 h-24 flex items-center justify-center rounded-lg group-hover:bg-gray-100 transition-colors duration-300">
                                            <img
                                                src={client.image}
                                                alt={`${client.name} logo`}
                                                className="max-w-full max-h-full object-contain filter group-hover:brightness-110 transition-all duration-300"
                                                onError={(e) => {
                                                    // Fallback to icon if image fails to load
                                                    e.target.style.display = 'none';
                                                    e.target.nextSibling.style.display = 'block';
                                                }}
                                            />
                                            <client.icon
                                                className="text-4xl text-primary group-hover:text-secondary transition-colors duration-300 hidden"
                                                style={{ display: 'none' }}
                                            />
                                        </div>
                                    </div>
                                    <h4 className="font-bold text-gray-800 text-sm group-hover:text-primary transition-colors duration-300">
                                        {client.name}
                                    </h4>
                                    <p className="text-xs text-gray-500 mt-1">{client.category}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Industry Categories */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="mb-16"
                >
                    <motion.h3
                        variants={itemVariants}
                        className="text-2xl md:text-3xl font-bold text-center text-primary mb-12"
                    >
                        Industries We Serve
                    </motion.h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[
                            {
                                title: "Oil & Gas",
                                count: "3 Partners",
                                icon: FaOilCan,
                                color: "bg-red-500",
                                type: "oil-gas"
                            },
                            {
                                title: "Engineering & Construction",
                                count: "3 Partners",
                                icon: FaCogs,
                                color: "bg-blue-500",
                                type: "engineering"
                            },
                            {
                                title: "Hospitality",
                                count: "2 Partners",
                                icon: FaHotel,
                                color: "bg-green-500",
                                type: "hospitality"
                            },
                            {
                                title: "Chemicals & Manufacturing",
                                count: "2 Partners",
                                icon: FaIndustry,
                                color: "bg-purple-500",
                                type: "chemicals"
                            },
                            {
                                title: "Infrastructure",
                                count: "1 Partner",
                                icon: FaBuilding,
                                color: "bg-orange-500",
                                type: "infrastructure"
                            }
                        ].map((industry, index) => (
                            <motion.div
                                key={index}
                                variants={itemVariants}
                                onClick={() => handleIndustryClick(industry.type)}
                                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group cursor-pointer"
                                whileHover={{ y: -3 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <div className="flex items-center gap-4">
                                    <div className={`w-12 h-12 ${industry.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                                        <industry.icon className="text-white text-xl" />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 group-hover:text-primary transition-colors duration-300">
                                            {industry.title}
                                        </h4>
                                        <p className="text-gray-500 text-sm">{industry.count}</p>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>

                {/* Business Hours Info Section */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="mb-16"
                >
                    <motion.div
                        variants={itemVariants}
                        className="bg-gradient-to-r from-secondary/10 to-secondary/5 rounded-3xl p-8 border border-secondary/20"
                    >
                        <div className="text-center">
                            <div className="flex items-center justify-center gap-3 mb-6">
                                <FaClock className="text-primary text-2xl" />
                                <h3 className="text-2xl font-bold text-primary">Business Hours</h3>
                            </div>
                            <div className="max-w-2xl mx-auto">
                                <p className="text-gray-700 mb-4">
                                    Our professional team is available to serve our valued partners during business hours
                                </p>
                                <div className="grid md:grid-cols-2 gap-4 text-center">
                                    <div className="bg-white/70 rounded-xl p-4">
                                        <p className="font-bold text-primary mb-1">Saturday - Thursday</p>
                                        <p className="text-gray-600">8:00 AM - 1:00 PM</p>
                                        <p className="text-gray-600">4:00 PM - 8:30 PM</p>
                                    </div>
                                    <div className="bg-white/70 rounded-xl p-4">
                                        <p className="font-bold text-primary mb-1">Friday</p>
                                        <p className="text-gray-600">Closed</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>

                {/* CTA Section */}
                <motion.div
                    initial="hidden"
                    animate={inView ? "visible" : "hidden"}
                    variants={containerVariants}
                    className="text-center"
                >
                    <motion.div
                        variants={itemVariants}
                        className="bg-gradient-to-br from-gray-800 via-gray-900 to-black rounded-3xl p-10 md:p-12 shadow-2xl"
                    >
                        <h3 className="text-3xl md:text-4xl font-bold text-white mb-6">
                            Partner With AKTP
                        </h3>
                        <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
                            Join our network of satisfied clients and experience the quality and reliability that makes us the preferred choice for industrial solutions across 20+ years of experience.
                        </p>

                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleBecomePartner}
                                className="bg-secondary text-primary px-8 py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 shadow-xl hover:bg-yellow-400 transition-all duration-300 cursor-pointer"
                            >
                                <FaHandshake />
                                Become a Partner
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleViewCaseStudies}
                                className="border-2 border-white text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-white hover:text-primary transition-all duration-300 cursor-pointer"
                            >
                                View Case Studies
                            </motion.button>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

const BrandImage = ({ brand, index }) => {
    const [currentSourceIndex, setCurrentSourceIndex] = React.useState(0);
    const [imageLoaded, setImageLoaded] = React.useState(false);

    const handleImageError = () => {
        if (currentSourceIndex < brand.sources.length - 1) {
            setCurrentSourceIndex(currentSourceIndex + 1);
        }
    };

    const handleImageLoad = () => {
        setImageLoaded(true);
    };

    return (
        <div
            key={`${brand.id}-${index}`}
            className="flex-shrink-0 w-32 h-20 md:w-36 md:h-24 flex items-center justify-center mx-2 cursor-pointer hover:scale-110 transition-transform duration-300 bg-white/70 rounded-lg border border-gray-200/30 shadow-sm"
        >
            {!imageLoaded && currentSourceIndex >= brand.sources.length - 1 ? (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                    Brand {brand.id}
                </div>
            ) : (
                <img
                    src={brand.sources[currentSourceIndex]}
                    alt={brand.alt}
                    className="max-w-full max-h-full object-contain filter hover:brightness-110 transition-all duration-300 p-2"
                    onError={handleImageError}
                    onLoad={handleImageLoad}
                    loading="lazy"
                />
            )}
        </div>
    );
};

export default BrandsPartner;